import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { getContractMinStartDate, getStartDateRange } from 'features/registration/utils'
import Router from 'next/router'
import { RootState, store } from 'store'
import { updateMarketingData } from 'store/marketing/slice'
import { MarketingDataKeys } from 'store/marketing/types'
import { Language } from 'types/language'
import { Producer, State } from 'types/producer'
import { getCapacityState, getProducersApiHelper } from 'utils/producers'
import { routes } from './routes'
import { AllProducts, ProductData } from 'types/product-data'
import { checkHasInjectionUsageFields, determineSimulationUsage, requestAndSavePropositions } from 'features/simulation/utils'
import {
  goToStep,
  selectProposition,
  setDidSimulation,
  setFlowAlternative,
  setItsmeOverwrittenByKey,
  setProductForSpecificSimulation,
  setShowPlentyPropositions,
  setStartedFunnelFromPath,
  updateAllInputs,
  updateFixedPrices,
  updateInput
} from 'store/customer/slice'
import { SimulationSteps } from 'features/simulation/types'
import {
  CustomerFlows,
  DeliveryAddressFieldKeys,
  FlowAlternatives,
  InputGroupKeys,
  MeterDetailsFieldKeys,
  PersonalDataFieldKeys,
  SimulationFieldKeys
} from 'store/customer/enums'
import { RegistrationSteps } from 'features/registration/types'
import { INTERNAL_REGISTRATION_URL_PARAMS, InternalRegistrationUrlParams } from 'constants/customer-flow'
import { CommonGTMEvents, CommonTrackingParams } from 'types/tracking'
import { StartSimulationOrRegistrationParams } from 'types/simulation'
import { identifyUser, setPostHogPersonProperties, trackEvent, trackGTMEvent } from 'utils/tracking'
import { addLogBreadcrumb, log, setLogContext, setLogUser } from 'utils/logging'
import { Criticality, Flow, Severity } from 'types/logging'
import { removeSpacesAndDots, shallowCompareObjects } from 'utils/helpers'
import axios from 'axios'
import { CheckPromoReferralCodeResponse, GetDataByOpportunityIdResponse, NextApiResponseBody } from 'types/bolt-api/request'
import { CodeTypes } from 'types/promo-referral-codes'
import {
  COUNTER_TYPES,
  CUSTOMER_TYPES,
  DEFAULT_PRODUCT,
  EL_PRODUCT_CODES,
  ELProduct,
  ENERGY_TYPES,
  GAS_PRODUCT_CODES,
  IDENTIFICATION_METHODS,
  METER_TYPES,
  NGProduct,
  OPPORTUNITY_STAGE_NAMES,
  OpportunityStageName,
  Product,
  PRODUCTS,
  PRODUCTS_CONFIG,
  SIMULATION_TYPES,
  SimulationType,
  SITUATIONS,
  Usage
} from '@boltenergy-be/shared'
import { convertKvaToInjection } from 'utils/conversion'
import { clearSessionStorage, setSessionStorageItem } from 'utils/sessionStorage'
import { ItsmePrefilledKeys } from 'store/customer/types'
import { EVENT_CATEGORIES } from 'constants/tracking'
import { formatPhoneToE164 } from '@boltenergy-be/design-system/utils'
import { updateOpportunityId } from 'store/app/slice'
import boltBackend from 'lib/bolt-backend'

dayjs.extend(isBetween)
dayjs.extend(isSameOrBefore)
dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Checks if the given simulation type is EAV or Fixed
 *
 * @param {SimulationType} simulationType
 * @returns {boolean}
 */
export const checkIsEavOrFixed = (simulationType: SimulationType): boolean => {
  return simulationType === SIMULATION_TYPES.fixedAmount || simulationType === SIMULATION_TYPES.eav
}

/**
 * Fetches the data for the given opportunity id & push it into the store
 *
 * @param {string} opportunityId
 * @param {Language} locale
 * @param {Partial<Record<ItsmePrefilledKeys, 'string'>>} overwriteData
 */
export const getDataByOpportunityId = async (
  opportunityId: string,
  locale: Language,
  overwriteData?: Partial<Record<ItsmePrefilledKeys, string>>
) => {
  const redirectUrlParams: Partial<Record<InternalRegistrationUrlParams, string>> = {}

  if (opportunityId) {
    // Save the opportunityId in the store
    store.dispatch(updateOpportunityId({ opportunityId }))

    // Destructure some store values
    const {
      customer: {
        inputs: { billing, meterDetails, overview, simulation },
        flowAlternative
      },
      marketing: { emailConfirmationFlow, noEanFlow, creditCheckConfirmationFlow }
    } = store.getState() as RootState

    clearSessionStorage()

    addLogBreadcrumb('getDataByOpportunityId: fetching', {
      opportunityId,
      flow: emailConfirmationFlow ? 'email_confirmation' : noEanFlow ? 'no_ean' : creditCheckConfirmationFlow ? 'credit_check' : 'default'
    })

    // Fetch the no EAN data
    const {
      data: { data: opportunityIdDataRes }
    } = await axios.get<NextApiResponseBody<GetDataByOpportunityIdResponse>>(`/api/prospects/${opportunityId}`)
    const prospect = opportunityIdDataRes?.prospect

    if (!prospect) {
      addLogBreadcrumb('getDataByOpportunityId: failed', { opportunityId })
      log({
        error: new Error('No data found for opportunityId'),
        identifier: `[${Flow.REGISTRATION}:getDataByOpportunityId]`,
        metaData: { prospect: { opportunityId } }
      })
      return
    }

    // Format the phone number to E.164 if it exists in the inputs
    if (prospect.inputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.MOBILE_PHONE]) {
      const phone = prospect.inputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.MOBILE_PHONE]
      const strippedPhone = removeSpacesAndDots(phone)
      prospect.inputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.MOBILE_PHONE] = formatPhoneToE164(strippedPhone)
    }

    const { fixedPrices, forcedAmount, marketingData, producerSlug, inputs, product, pricelistUrls, stage } = prospect

    // Identify the existing prospect with the stable opportunity ID.
    identifyUser(opportunityId, {
      email: inputs[InputGroupKeys.PERSONAL_DATA]?.[PersonalDataFieldKeys.EMAIL],
      [CommonTrackingParams.IDENTIFICATION_METHOD]: marketingData.identificationMethod,
      [CommonTrackingParams.CUSTOMER_TYPE]: inputs.personalData.isCompany ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential,
      [CommonTrackingParams.REGION]: inputs.simulation.region,
      [CommonTrackingParams.SOURCE_TYPE]: marketingData.sourceType
    })

    // Identify user in Sentry
    setLogUser({
      email: inputs[InputGroupKeys.PERSONAL_DATA]?.[PersonalDataFieldKeys.EMAIL],
      name: `${inputs[InputGroupKeys.PERSONAL_DATA]?.[PersonalDataFieldKeys.FIRSTNAME]} ${inputs[InputGroupKeys.PERSONAL_DATA]?.[PersonalDataFieldKeys.LASTNAME]}`
    })

    const shouldIdentifyWithItsme = false
    // ItsMe is temporary disable due to acquisition block project.
    // const shouldIdentifyWithItsme = checkShouldIdentifyWithItsme(
    //   inputs[InputGroupKeys.SIMULATION][SimulationFieldKeys.REGION],
    //   inputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.IS_COMPANY],
    //   {
    //     [MarketingDataKeys.CONFIRMATION_FROM_PORTAL_REGISTRATION]: marketingData.confirmationFromPortalRegistration,
    //     [MarketingDataKeys.SKIP_ITSME]: skipItsme
    //   }
    // )
    const wronglyAssignedItsmeIdentification = noEanFlow
      ? false
      : marketingData.identificationMethod === IDENTIFICATION_METHODS.itsme && !shouldIdentifyWithItsme

    // Set the authentication method if it's not the default
    if (marketingData.identificationMethod !== IDENTIFICATION_METHODS.default) {
      store.dispatch(
        updateMarketingData({
          key: MarketingDataKeys.IDENTIFICATION_METHOD,
          value: wronglyAssignedItsmeIdentification ? IDENTIFICATION_METHODS.default : marketingData.identificationMethod
        })
      )
    }

    // Disable itsme flow alternative when not necessary due to data from opportunity
    if ((flowAlternative[FlowAlternatives.ITSME] && wronglyAssignedItsmeIdentification) || noEanFlow) {
      store.dispatch(setFlowAlternative({ key: FlowAlternatives.ITSME, value: false }))
    }

    // Update the marketing store parameters
    if (marketingData.confirmationFromPortalRegistration) {
      store.dispatch(
        updateMarketingData({
          key: MarketingDataKeys.CONFIRMATION_FROM_PORTAL_REGISTRATION,
          value: marketingData.confirmationFromPortalRegistration
        })
      )
    }

    if (marketingData.simulationType) {
      store.dispatch(
        updateMarketingData({
          key: MarketingDataKeys.SIMULATION_TYPE,
          value: [marketingData.simulationType]
        })
      )
    }

    if (marketingData.sourceType) {
      store.dispatch(updateMarketingData({ key: MarketingDataKeys.SOURCE_TYPE, value: marketingData.sourceType }))
    }

    // Check if contract start date is still valid & change if necessary
    const { max, min, initial } = getStartDateRange(inputs.meterDetails.situation)
    const contractMinStartDate = getContractMinStartDate(
      min,
      inputs.meterDetails.contractStartDate,
      marketingData.partnerID,
      marketingData.simulationSalesOffice
    )

    // Check if switchDate from market has to be checked
    const contractStartDate = inputs.meterDetails.contractStartDate
    const contractStartDateWithinValidRange = dayjs(contractStartDate).isBetween(contractMinStartDate, max, 'day', '[]')
    const hasElEanNumber = !!inputs.meterDetails.electricity?.ean // electricity is always required

    if (!contractStartDateWithinValidRange) {
      if (hasElEanNumber && stage === OPPORTUNITY_STAGE_NAMES.TO_BE_CONFIRMED && emailConfirmationFlow) {
        // Cross-check market switch date with contract start date
        const { success, data: eanData } = await boltBackend.getEanData({
          contractStartDate,
          ean: inputs.meterDetails.electricity.ean!.toString(),
          energyType: ENERGY_TYPES.electricity
        })

        if (!success || !eanData?.switchDate) {
          // Same path as no EAN number
          inputs.meterDetails.contractStartDate = initial
        } else if (!dayjs(eanData.switchDate).isSame(dayjs(contractStartDate), 'day')) {
          addLogBreadcrumb('getDataByOpportunityId: market switch date mismatch', { contractStartDate, eanData })

          store.dispatch(goToStep({ flow: CustomerFlows.REGISTRATION, step: RegistrationSteps.METER_DETAILS }))
          inputs.meterDetails.situation = SITUATIONS.move
          redirectUrlParams[INTERNAL_REGISTRATION_URL_PARAMS.INVALID_MARKET_SWITCH_DATE] = eanData.switchDate
        }
      } else {
        inputs.meterDetails.contractStartDate = initial
      }
    }

    // Update the fixed prices if necessary
    if (fixedPrices) store.dispatch(updateFixedPrices(fixedPrices))

    // Update the selected product
    const productType = getProductFromElectricityProduct(product)
    if (!productType) return

    store.dispatch(selectProposition({ productType }))
    if (PRODUCTS_CONFIG[productType].isPlentyProduct) {
      store.dispatch(setShowPlentyPropositions(true))
    }

    // Set the customer store inputs
    store.dispatch(
      updateAllInputs({
        inputs: {
          ...inputs,
          billing: {
            ...billing,
            ...inputs[InputGroupKeys.BILLING]
          },
          deliveryAddress: {
            needsGas: inputs[InputGroupKeys.DELIVERY_ADDRESS][DeliveryAddressFieldKeys.NEEDS_GAS],
            address: inputs[InputGroupKeys.DELIVERY_ADDRESS][DeliveryAddressFieldKeys.ADDRESS]
          },
          meterDetails: {
            ...inputs[InputGroupKeys.METER_DETAILS],
            contractStartDate: inputs[InputGroupKeys.METER_DETAILS].contractStartDate ?? meterDetails.contractStartDate
          },
          simulation: {
            ...simulation,
            ...inputs[InputGroupKeys.SIMULATION],
            ...(marketingData.simulationType && {
              chosenSimulationType: marketingData.simulationType
            })
          },
          overview
        }
      })
    )

    // Has to run after the inputs above are in the store: the usage carries a solarKva that is only determined for
    // some regions, and reading a region the opportunity hasn't set yet drops it from every simulation that follows.
    const determinedUsageBasedOnOpportunityInputs = determineSimulationUsage(
      inputs[InputGroupKeys.SIMULATION][SimulationFieldKeys.USAGE].grade,
      {
        ...inputs.simulation.usage,
        counterType: inputs[InputGroupKeys.METER_DETAILS][MeterDetailsFieldKeys.COUNTER_TYPE],
        hasExclNight: inputs[InputGroupKeys.SIMULATION][SimulationFieldKeys.HAS_EXCLUSIVE_NIGHT_METER],
        hasSolarPanels: inputs[InputGroupKeys.METER_DETAILS][MeterDetailsFieldKeys.HAS_SOLAR_PANELS],
        meterType: inputs[InputGroupKeys.SIMULATION][SimulationFieldKeys.METER_TYPE],
        needsGas: inputs[InputGroupKeys.DELIVERY_ADDRESS][DeliveryAddressFieldKeys.NEEDS_GAS],
        solarKva: inputs.simulation.usage.solarKva,
        solarPanels: inputs[InputGroupKeys.SIMULATION][SimulationFieldKeys.SOLAR_PANELS],
        walloniaSolarPanelsSince2024: inputs[InputGroupKeys.METER_DETAILS][MeterDetailsFieldKeys.WALLONIA_SOLAR_PANELS_SINCE_2024]
      }
    )

    store.dispatch(
      updateInput({
        group: InputGroupKeys.SIMULATION,
        key: SimulationFieldKeys.USAGE,
        value: determinedUsageBasedOnOpportunityInputs
      })
    )

    // If overwriteData is passed, compare prospect and the given data for mismatches and update where necessary.
    // Currently only for itsme.
    if (overwriteData && !wronglyAssignedItsmeIdentification) {
      const prospectFields = {
        [PersonalDataFieldKeys.FIRSTNAME]: inputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.FIRSTNAME],
        [PersonalDataFieldKeys.LASTNAME]: inputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.LASTNAME],
        [PersonalDataFieldKeys.EMAIL]: inputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.EMAIL],
        [PersonalDataFieldKeys.MOBILE_PHONE]: inputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.MOBILE_PHONE]
      }

      // Compare both object with normalized values and ignore non overlapping keys
      const keysToOverwrite = shallowCompareObjects<ItsmePrefilledKeys>(prospectFields, overwriteData, {
        normalize: true,
        ignoreNonOverlappingKeys: true
      })

      // Loop over the keys to overwrite, keep track of the overwriting in the store & update the field in the input state
      keysToOverwrite.forEach((key) => {
        store.dispatch(setItsmeOverwrittenByKey({ key, value: true }))
        store.dispatch(updateInput({ group: InputGroupKeys.PERSONAL_DATA, key, value: overwriteData?.[key] }))
      })

      if (keysToOverwrite.includes(PersonalDataFieldKeys.EMAIL)) {
        await fetch('https://hooks.zapier.com/hooks/catch/5187268/uk3enih/', {
          body: JSON.stringify({
            opportunityId,
            emailFromItsme: overwriteData?.[PersonalDataFieldKeys.EMAIL]
          }),
          method: 'POST'
        })

        trackEvent(
          { category: EVENT_CATEGORIES.SUBSCRIPTION, object: 'itsme-confirmation-with-email-mismatch', action: 'catch' },
          { region: simulation.region, emailConfirmation: true, opportunityId }
        )
      }
    }

    // Save the price list urls
    if (pricelistUrls) store.dispatch(updateMarketingData({ key: MarketingDataKeys.PRICELIST_URLS, value: pricelistUrls }))

    // Check if Fixed but no fixed prices
    const fixedWithoutPrices =
      marketingData.simulationType === SIMULATION_TYPES.fixedAmount && (!fixedPrices || fixedPrices.electricity === null)

    // Check if not came through portal && (not EAV/Fixed || Fixed without prices)
    if (!marketingData.confirmationFromPortalRegistration && (!checkIsEavOrFixed(marketingData.simulationType) || fixedWithoutPrices)) {
      // Set simulationType to Pricing simulation if fixed without prices & no solar panels
      if (fixedWithoutPrices) {
        store.dispatch(
          updateInput({
            group: InputGroupKeys.SIMULATION,
            key: SimulationFieldKeys.CHOSEN_SIMULATION_TYPE,
            value: SIMULATION_TYPES.pricingSimulation
          })
        )

        // If the user has solar panels, solarKva and digital meter in the opportunityId, convert solarKva to injection
        if (
          inputs.meterDetails.hasSolarPanels &&
          inputs.simulation.usage.solarKva &&
          inputs.meterDetails.counterType === COUNTER_TYPES.digital
        ) {
          const needsInjection = checkHasInjectionUsageFields({
            counterType: inputs.meterDetails.counterType,
            hasSolarPanels: inputs.meterDetails.hasSolarPanels
          })

          if (needsInjection) {
            // convert solarKva to injection
            const injection = convertKvaToInjection(inputs.simulation.usage.solarKva)
            const injectionDay = injection * 0.66

            store.dispatch(
              updateInput({
                group: InputGroupKeys.SIMULATION,
                key: SimulationFieldKeys.USAGE,
                value: {
                  ...inputs.simulation.usage,
                  // Update the usage when injection is expected
                  electricity: {
                    ...inputs.simulation.usage.electricity,
                    injection:
                      inputs.simulation.meterType === METER_TYPES.single
                        ? {
                            single: Math.round(injection)
                          }
                        : {
                            double: {
                              day: Math.round(injectionDay),
                              night: Math.round(injection - injectionDay)
                            }
                          }
                  }
                } as Usage
              })
            )
          }
        }
      }

      // Request and save propositions
      if (stage !== OPPORTUNITY_STAGE_NAMES.CLOSED_WON) {
        void requestAndSavePropositions({
          products: [productType]
        })
      }

      // Set didSimulation to true
      store.dispatch(setDidSimulation())
    }

    // Check if redirect to meter details is necessary during confirmation flow
    // when there is a move for an analog meter in the past & no meterNumbers are given
    if (emailConfirmationFlow) {
      const { meterDetails, deliveryAddress } = inputs

      if (
        meterDetails.counterType === COUNTER_TYPES.analog &&
        meterDetails.situation === SITUATIONS.move &&
        dayjs(meterDetails.contractStartDate).isSameOrBefore(dayjs(), 'day') &&
        (!meterDetails.electricity.meterNumber || (deliveryAddress.needsGas && !meterDetails.gas.meterNumber))
      ) {
        // Set the registration step to overview (user only needs to confirm)
        store.dispatch(goToStep({ flow: CustomerFlows.REGISTRATION, step: RegistrationSteps.METER_DETAILS }))
      }
    }

    // Update the forced amount if necessary
    if (forcedAmount) {
      store.dispatch(updateInput({ group: InputGroupKeys.SIMULATION, key: SimulationFieldKeys.FORCED_AMOUNT, value: forcedAmount }))
    }

    setLogContext('get_data_by_opportunity_id', {
      opportunityId,
      opportunityIdDataRes,
      updatedStore: store.getState() as RootState
    })

    addLogBreadcrumb('getDataByOpportunityId: success', { opportunityId })

    // Redirect to the correct producer page
    void redirectToCorrectProducerPage(stage, producerSlug, locale, redirectUrlParams)
  } else {
    throw new Error('No opportunity ID given.')
  }
}

/**
 * Non-throwing variant of getDataByOpportunityId for callers that render while the data loads.
 * Without it a rejection escapes the render as an untagged unhandled promise rejection.
 *
 * @param {string} opportunityId
 * @param {Language} locale
 */
export const loadDataByOpportunityId = (opportunityId: string, locale: Language): void => {
  getDataByOpportunityId(opportunityId, locale).catch((error) => {
    log({
      error,
      identifier: `[${Flow.REGISTRATION}:getDataByOpportunityId]`,
      criticality: Criticality.HIGH
    })
  })
}

/**
 * Returns the electricity product
 *
 * @param {AllProducts} allProducts
 * @param {Product} product
 * @returns {ProductData | undefined}
 */
export const getElectricityProduct = (allProducts: AllProducts, product?: Product): ProductData | undefined => {
  const {
    customer,
    marketing: { availableProducts }
  } = store.getState()
  const { region } = customer.inputs[InputGroupKeys.SIMULATION]
  const elProduct: ELProduct =
    EL_PRODUCT_CODES[
      (product || customer.propositions?.selectedProposition || (region && availableProducts?.[region]?.[0]) || DEFAULT_PRODUCT) as Product
    ]

  return allProducts?.electricity[elProduct]
}

/**
 * Returns the gas product
 *
 * @param {AllProducts} allProducts
 * @param {Product} product
 * @returns {ProductData | undefined}
 */
export const getGasProduct = (allProducts: AllProducts, product?: Product): ProductData | undefined => {
  const {
    customer,
    marketing: { availableProducts }
  } = store.getState() as RootState
  const { region } = customer.inputs[InputGroupKeys.SIMULATION]
  const ngProduct: NGProduct =
    GAS_PRODUCT_CODES[
      (product || customer.propositions?.selectedProposition || (region && availableProducts?.[region]?.[0]) || DEFAULT_PRODUCT) as Product
    ]

  return allProducts?.gas[ngProduct]
}

/**
 * Returns the correct Product enum value for the given ELProduct enum value
 *
 * @param {ELProduct} product
 * @returns {Product}
 */
export const getProductFromElectricityProduct = (product: ELProduct): Product | undefined => {
  switch (product) {
    case EL_PRODUCT_CODES.EarlyBirdOn:
      return PRODUCTS.earlyBirdOnline

    case EL_PRODUCT_CODES.EarlyBirdOff:
      return PRODUCTS.earlyBirdOffline

    case EL_PRODUCT_CODES.VariableOn:
      return PRODUCTS.variableOnline

    case EL_PRODUCT_CODES.VariableOff:
      return PRODUCTS.variableOffline

    case EL_PRODUCT_CODES.Go:
      return PRODUCTS.go

    case EL_PRODUCT_CODES.Fix:
      return PRODUCTS.fix

    case EL_PRODUCT_CODES.PlentyFix:
      return PRODUCTS.plentyFix

    case EL_PRODUCT_CODES.PlentyVariableOff:
      return PRODUCTS.plentyVariableOffline

    case EL_PRODUCT_CODES.PlentyVariableOn:
      return PRODUCTS.plentyVariableOnline

    default: {
      log({
        error: 'Mapping is missing a product',
        identifier: `[${Flow.REGISTRATION}:getProductFromElectricityProduct]`,
        criticality: Criticality.CRITICAL,
        severity: Severity.ERROR,
        metaData: { productMapping: { product } }
      })
    }
  }
}

/**
 * Fetches a promo code, sets it in the store & returns the code and whether it's valid or not
 *
 * @param {string} code
 */
export const setPromoOrReferralCodeInStore = async (code: string): Promise<{ code?: string; valid: boolean }> => {
  if (!code) {
    return {
      code,
      valid: false
    }
  }

  const { data } = await axios.get<NextApiResponseBody<CheckPromoReferralCodeResponse>>(`/api/website/promo-referral-code?code=${code}`)

  if (!data?.data) {
    return {
      code,
      valid: false
    }
  }

  const checkedPromoReferralCode = data.data
  const isPromoCode = checkedPromoReferralCode.type === CodeTypes.PROMO

  if (checkedPromoReferralCode.valid) {
    store.dispatch(
      updateInput({
        group: InputGroupKeys.SIMULATION,
        ...(isPromoCode
          ? {
              key: SimulationFieldKeys.PROMO_CODE,
              value: checkedPromoReferralCode.promoCode
            }
          : {
              key: SimulationFieldKeys.REFERRAL_CODE,
              value: checkedPromoReferralCode.referralCode
            })
      })
    )
  }

  return {
    code: isPromoCode ? checkedPromoReferralCode.promoCode?.code : checkedPromoReferralCode.referralCode?.code,
    valid: checkedPromoReferralCode.valid
  }
}

/**
 * Start simulation or registration based on store
 * @param {StartSimulationOrRegistrationParams} params
 */
export const startSimulationOrRegistration = async (params?: StartSimulationOrRegistrationParams) => {
  // Redux store
  const { selectedProposition, simulatedPropositions } = store.getState().customer.propositions
  const { didSimulation } = store.getState().customer.flows.simulation
  const { chosenSimulationType, region } = store.getState().customer.inputs[InputGroupKeys.SIMULATION]
  const { selectedProducer } = store.getState().app
  const { availableProducts } = store.getState().marketing

  // Save the current page in the store in case they want to return during the flow
  const currentPath = window.location.pathname
  const currentQuery = window.location.search
  store.dispatch(setStartedFunnelFromPath(`${currentPath}${currentQuery}`))

  // Store the region on the current anonymous person for the proposition-step A/B test.
  setPostHogPersonProperties({
    [CommonTrackingParams.REGION]: region
  })

  // Constants
  const isEavOrFixed = checkIsEavOrFixed(chosenSimulationType)

  // Set the simulation sales office if passed
  if (params?.simulationSalesOffice) {
    store.dispatch(updateMarketingData({ key: MarketingDataKeys.SIMULATION_SO, value: params?.simulationSalesOffice }))
  }

  // Go straight to registration if it's an EAV or Fixed flow
  if (isEavOrFixed) {
    // select the default product
    store.dispatch(selectProposition({ productType: region ? availableProducts?.[region]?.[0] || DEFAULT_PRODUCT : DEFAULT_PRODUCT }))
    return Router.push(routes(Router.locale || Language.DUTCH, selectedProducer).registration)
  }

  // Fetch propositions if the user has already done a simulation and the propositions for this product have not been fetched yet
  if (didSimulation && params?.specificProduct && simulatedPropositions && !simulatedPropositions?.[params.specificProduct]) {
    void requestAndSavePropositions({
      products: [params.specificProduct]
    })
  }

  // Go straight to registration if the user has already done a simulation and the selected proposition is the same as the specific product
  if (didSimulation && selectedProposition && selectedProposition === params?.specificProduct) {
    return Router.push(
      selectedProducer ? routes(Router.locale || Language.DUTCH, selectedProducer).registration : routes(Router.locale!).producers
    )
  }

  // Set or reset specific product for simulation if available
  if (params?.specificProduct) {
    store.dispatch(setProductForSpecificSimulation(params.specificProduct))
  } else {
    store.dispatch(setProductForSpecificSimulation(null))
  }

  // Track 'Simulation Started' event
  trackGTMEvent(CommonGTMEvents.SIMULATION_STARTED)
  trackEvent(
    { category: 'simulation', action: 'start' },
    {
      [CommonTrackingParams.FROM_PAGE]: params?.fromPage || currentPath,
      [CommonTrackingParams.SELECTED_PRODUCT]: params?.specificProduct
    }
  )

  return openSimulation(
    // Go to usage step if there is a postal code & the current step is not PostalCodeStep or EnergyTypeStep
    params?.startFromUsage ? SimulationSteps.USAGE : undefined
  )
}

// HELPER FUNCTIONS

/**
 * Opens the simulation page
 * optional: pass the simulationStep to go to a specific step
 * @param {SimulationSteps} from
 */
const openSimulation = (from?: SimulationSteps) => {
  if (from) store.dispatch(goToStep({ flow: CustomerFlows.SIMULATION, step: from }))

  return Router.push({ pathname: routes(Router.locale || Language.DUTCH).simulation })
}

/**
 * Redirects to the correct producer page
 *
 * @param {OPPORTUNITY_STAGE_NAMES} stage
 * @param {string} producerSlug
 * @param {Language} locale
 * @param {Record<string, string>} redirectParams
 */
const redirectToCorrectProducerPage = async (
  stage: OpportunityStageName,
  producerSlug: string,
  locale: Language,
  redirectParams: Record<string, string>
) => {
  // Get values from the store
  const { app, customer, marketing } = store.getState() as RootState
  const { selectedProducer } = app
  const { flows, inputs } = customer
  const { emailConfirmationFlow, noEanFlow, creditCheckConfirmationFlow } = marketing

  if (emailConfirmationFlow && stage !== OPPORTUNITY_STAGE_NAMES.TO_BE_CONFIRMED) {
    if (stage === OPPORTUNITY_STAGE_NAMES.CLOSED_WON) {
      await Router.push({
        pathname: routes(locale, producerSlug).registration,
        query: { [INTERNAL_REGISTRATION_URL_PARAMS.IS_CLOSED_WON]: true }
      })
      return
    }

    if (stage === OPPORTUNITY_STAGE_NAMES.WAITING_FOR_PLENTY_INVESTMENT) {
      store.dispatch(setShowPlentyPropositions(true))
      store.dispatch(goToStep({ flow: CustomerFlows.REGISTRATION, step: RegistrationSteps.CONFIRMATION }))

      await Router.push({ pathname: routes(locale, producerSlug).registration })
      return
    }

    await Router.push({
      pathname: routes(locale, producerSlug).registration,
      query: { [INTERNAL_REGISTRATION_URL_PARAMS.IS_NOT_TO_BE_CONFIRMED]: true }
    })
    return
  }

  if (noEanFlow && stage !== OPPORTUNITY_STAGE_NAMES.NO_EAN) {
    store.dispatch(goToStep({ flow: CustomerFlows.REGISTRATION, step: RegistrationSteps.OVERVIEW }))
    await Router.push({
      pathname: routes(locale, producerSlug).registration,
      query: { [INTERNAL_REGISTRATION_URL_PARAMS.IS_INVALID_NO_EAN_FLOW]: true }
    })
    return
  }

  if (creditCheckConfirmationFlow && stage !== OPPORTUNITY_STAGE_NAMES.CREDIT_CHECK) {
    store.dispatch(goToStep({ flow: CustomerFlows.REGISTRATION, step: RegistrationSteps.OVERVIEW }))
    await Router.push({
      pathname: routes(locale, producerSlug).registration,
      query: { [INTERNAL_REGISTRATION_URL_PARAMS.IS_INVALID_CREDIT_CHECK_FLOW]: true }
    })
    return
  }

  if (!producerSlug) {
    producerSlug =
      selectedProducer || (await getProducersApiHelper({ language: locale, params: { enabled: true, soldOut: false } }))?.[0]?.slug
  }

  setSessionStorageItem('producerSlug', producerSlug)

  // Check if producer is not sold out (take simulation data into account)
  // If so, navigate to another producer which is not soldOut and also not almostSoldOut
  // If not, navigate to registration page of current producer
  const { data } = await axios.get<NextApiResponseBody<Producer>>(`/api/producers/${producerSlug}`)
  const capacityState = getCapacityState(data.data!, inputs.simulation, flows.simulation.didSimulation)
  const { soldOut } = capacityState

  if (soldOut) {
    const producers = await getProducersApiHelper({ language: locale, params: { enabled: true } })
    const availableProducer = producers.filter((producer: Producer) => {
      const capacityState = getCapacityState(producer, inputs.simulation, flows.simulation.didSimulation)
      const { soldOut, almostSoldOut } = capacityState

      if (!soldOut && !almostSoldOut && producer.state !== State.RESERVED) {
        return producer
      }
    })[0]

    if (availableProducer.slug) {
      await Router.push({
        pathname: routes(locale, availableProducer.slug).registration,
        query: emailConfirmationFlow ? redirectParams : undefined
      })
    } else {
      await Router.push(routes(locale).producers)
    }
  } else {
    await Router.push({
      pathname: routes(locale, producerSlug).registration,
      query: emailConfirmationFlow ? redirectParams : undefined
    })
  }
}
